import React, { useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { UserContext } from '../../context/UserContext';
import CommonLayout from '../../components/CommonLayout';
import DynamicFaIcon from '../../components/DynamicFaIcon';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import API from '../../config/axiosConfig';
import useApps from '../../hooks/useApps';
import {
  FaBook,
  FaUsers,
  FaClipboardList,
  FaBriefcaseMedical,
  FaSearch,
  FaTimes,
  FaCheck,
  FaFolderOpen
} from 'react-icons/fa';
import { confirmButton, cancelButton } from '../../components/Buttons';
import { getUserConfiguredApps, getAvailableApps } from './homepageUtils';
import './Homepage.scss';

const Homepage = () => {
  const { userConfig, updateUserConfig, permittedApps, user, hospitals } = useContext(UserContext);
  const { apps: allApps, loading: appsLoading } = useApps();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedTiles, setSelectedTiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [warningPosition, setWarningPosition] = useState({ x: 0, y: 0 });
  const [isButtonCompact, setIsButtonCompact] = useState(false);
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const tilesRef = useRef(null);

  // Get tiles from user config or use defaults - memoized to prevent recalculation
  const tiles = useMemo(() => {
    return getUserConfiguredApps(userConfig, permittedApps, user, hospitals, allApps);
  }, [userConfig, permittedApps, user, hospitals, allApps]);

  // Detect overlap between button and tiles on scroll/resize
  useEffect(() => {
    const checkOverlap = () => {
      // Skip on mobile - always compact
      if (window.innerWidth <= 768) {
        setIsButtonCompact(false); // Let CSS handle it
        return;
      }

      if (!buttonRef.current || !tilesRef.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tileElements = tilesRef.current.querySelectorAll('.homepage__tile');

      let hasOverlap = false;
      tileElements.forEach(tile => {
        const tileRect = tile.getBoundingClientRect();

        // Check if button overlaps with any tile
        if (!(buttonRect.right < tileRect.left ||
              buttonRect.left > tileRect.right ||
              buttonRect.bottom < tileRect.top ||
              buttonRect.top > tileRect.bottom)) {
          hasOverlap = true;
        }
      });

      setIsButtonCompact(hasOverlap);
    };

    checkOverlap();
    window.addEventListener('scroll', checkOverlap);
    window.addEventListener('resize', checkOverlap);

    return () => {
      window.removeEventListener('scroll', checkOverlap);
      window.removeEventListener('resize', checkOverlap);
    };
  }, [appsLoading]);

  // Get available tile options for modal - memoized to prevent recalculation
  const availableTileOptions = useMemo(() => {
    return getAvailableApps(allApps, user);
  }, [allApps, user]);

  // Initialize selected tiles when modal opens
  useEffect(() => {
    if (showConfigModal) {
      const currentTiles = userConfig?.homepage?.tiles?.length > 0
        ? userConfig.homepage.tiles
        : getUserConfiguredApps(userConfig, permittedApps, user, hospitals, allApps);

      setSelectedTiles(currentTiles.map(t => ({
        ...t,
        appId: t.appId?._id || t.appId
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfigModal]);

  // Search function with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true);
        try {
          // Get the hospital English name from the user's first role
          const userDepartment = user?.roles?.[0];
          const hospital = hospitals.find(h => h._id === userDepartment?.hospital);
          const hospitalEnglishName = hospital?.nameEnglish || 'apps';

          const response = await API.get(`/api/user-config/search?q=${encodeURIComponent(searchQuery)}&hospitalEnglishName=${encodeURIComponent(hospitalEnglishName)}`);
          setSearchResults(response.data);
          setSelectedSearchIndex(-1); // Reset selection when new results come in
        } catch (error) {
          console.error('Search failed:', error);
          setSearchResults(null);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults(null);
        setSelectedSearchIndex(-1);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, user, hospitals]);

  const getTileConfig = useCallback((type, appId = null) => {
    const configs = {
      protocols: {
        title: 'פרוטוקולים',
        icon: <FaBook />,
        url: '/protocols',
      },
      users: {
        title: 'משתמשים',
        icon: <FaUsers />,
        url: '/users',
      },
      checklists: {
        title: 'רשימות',
        icon: <FaClipboardList />,
        url: '/checklists',
      },
      cases: {
        title: 'מקרים רפואיים',
        icon: <FaBriefcaseMedical />,
        url: '/cases',
      },
      departmentDocuments: {
        title: 'מסמכי המחלקה',
        icon: <FaFolderOpen />,
        url: '/department-documents',
      }
    };

    if (type === 'app' && appId) {
      // Search in allApps instead of permittedApps to include department apps
      const app = allApps?.find(a => a._id === appId || a._id === appId._id);
      if (app) {
        const userDepartment = user?.roles?.[0];
        const hospital = hospitals.find(h => h._id === userDepartment?.hospital);
        const hospitalName = hospital?.nameEnglish || 'apps';

        return {
          title: app.title,
          icon: <DynamicFaIcon iconName={app.icon} />,
          url: `/main/${hospitalName}/${app.appToken}`,
        };
      }
    }

    return configs[type] || null;
  }, [allApps, user, hospitals]);

  const handleTileClick = useCallback((url) => {
    navigate(url);
  }, [navigate]);

  const handleSearchResultClick = useCallback((result) => {
    navigate(result.url);
    setSearchQuery('');
    setSearchResults(null);
  }, [navigate]);

  const toggleTile = useCallback((type, appId = null, event) => {
    const existingIndex = selectedTiles.findIndex(t => {
      if (appId) {
        return t.type === 'app' && (t.appId === appId || t.appId?._id === appId);
      }
      return t.type === type;
    });

    if (existingIndex >= 0) {
      setSelectedTiles(selectedTiles.filter((_, i) => i !== existingIndex));
      setShowLimitWarning(false);
    } else {
      // Check if we already have 6 tiles selected
      if (selectedTiles.length >= 6) {
        // Get the position of the clicked tile
        const rect = event.currentTarget.getBoundingClientRect();
        setWarningPosition({
          x: rect.left + rect.width / 2,
          y: rect.top
        });
        setShowLimitWarning(true);

        // Hide warning after 3 seconds
        setTimeout(() => {
          setShowLimitWarning(false);
        }, 3000);
        return;
      }
      setSelectedTiles([
        ...selectedTiles,
        {
          type,
          appId,
          order: selectedTiles.length
        }
      ]);
    }
  }, [selectedTiles]);

  const isTileSelected = useCallback((type, appId = null) => {
    return selectedTiles.some(t => {
      if (appId) {
        return t.type === 'app' && (t.appId === appId || t.appId?._id === appId);
      }
      return t.type === type;
    });
  }, [selectedTiles]);

  const handleSaveConfig = useCallback(async () => {
    setIsSaving(true);
    try {
      await updateUserConfig({
        homepage: {
          tiles: selectedTiles.map((t, index) => ({
            type: t.type,
            // Ensure we only save the ID string, not the full object
            appId: t.appId?._id || t.appId || null,
            order: index
          }))
        }
      });
      setShowConfigModal(false);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      alert('שגיאה בשמירת ההגדרות');
    } finally {
      setIsSaving(false);
    }
  }, [selectedTiles, updateUserConfig]);

  const hasSearchResults = searchResults && (
    searchResults.apps.length > 0 ||
    searchResults.protocols.length > 0 ||
    searchResults.cases.length > 0 ||
    searchResults.checklists.length > 0
  );

  // Flatten all search results into a single array for keyboard navigation
  const allSearchResults = searchResults
    ? [
        ...searchResults.apps,
        ...searchResults.protocols,
        ...searchResults.cases,
        ...searchResults.checklists
      ]
    : [];

  // Handle keyboard navigation in search
  const handleSearchKeyDown = useCallback((e) => {
    if (!hasSearchResults) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSearchIndex(prev =>
        prev < allSearchResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSearchIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedSearchIndex >= 0) {
      e.preventDefault();
      const selected = allSearchResults[selectedSearchIndex];
      if (selected) {
        handleSearchResultClick(selected);
      }
    } else if (e.key === 'Escape') {
      setSearchQuery('');
      setSearchResults(null);
      setSelectedSearchIndex(-1);
    }
  }, [hasSearchResults, allSearchResults, selectedSearchIndex, handleSearchResultClick]);

  // Edit icon SVG component
  const EditIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.1026 15.8735L9.0796 15.6025L14.6796 9.99752L11.9836 7.30052L6.3666 12.9175L6.1026 15.8735ZM13.3236 5.96152L16.0186 8.65652L17.9656 6.70652L15.2716 4.01252L13.3236 5.96152ZM4.2926 17.6855C4.0826 17.4755 3.9776 17.1835 4.0036 16.8875L4.3826 12.7175C4.4246 12.2605 4.6266 11.8295 4.9526 11.5035L13.9486 2.50752C14.6506 1.80252 15.9236 1.83752 16.6646 2.57652L19.4026 5.31452L19.4036 5.31552C20.1686 6.08152 20.1996 7.29952 19.4716 8.02952L10.4746 17.0265C10.1496 17.3515 9.7186 17.5535 9.2606 17.5955L5.0906 17.9745C5.0606 17.9765 5.0306 17.9775 4.9996 17.9775C4.7366 17.9775 4.4816 17.8735 4.2926 17.6855ZM19.9996 20.9775C19.9996 21.5275 19.5496 21.9775 18.9996 21.9775H4.9996C4.4506 21.9775 3.9996 21.5275 3.9996 20.9775C3.9996 20.4285 4.4506 19.9775 4.9996 19.9775H18.9996C19.5496 19.9775 19.9996 20.4285 19.9996 20.9775Z" fill="#103C6E"/>
    </svg>
  );

  return (
    <CommonLayout maxWidth="1200px">
      <div className="homepage">
        <h1 className="homepage__title">
          <span className="homepage__title-desktop">ברוכים הבאים ל mashlom.me</span>
          <span className="homepage__title-mobile">mashlom.me</span>
        </h1>

        {/* Search Box */}
        <div className="homepage__search">
          <div className="homepage__search-input-wrapper">
            <FaSearch className="homepage__search-icon" />
            <input
              type="text"
              placeholder="חיפוש באתר..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="homepage__search-input"
            />
          </div>

          {/* Search Results Dropdown */}
          {searchQuery.trim().length >= 2 && (
            <div className="homepage__search-results">
              {isSearching ? (
                <div className="homepage__search-loading">מחפש...</div>
              ) : hasSearchResults ? (
                <>
                  {searchResults.apps.length > 0 && (
                    <div className="homepage__search-category">
                      <div className="homepage__search-category-title">אפליקציות</div>
                      {searchResults.apps.map((app, idx) => {
                        const globalIndex = idx;
                        return (
                          <div
                            key={app._id}
                            className={`homepage__search-item ${selectedSearchIndex === globalIndex ? 'selected' : ''}`}
                            onClick={() => handleSearchResultClick(app)}
                          >
                            <span className="homepage__search-item-icon">
                              <DynamicFaIcon iconName={app.icon} />
                            </span>
                            <span className="homepage__search-item-text">{app.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {searchResults.protocols.length > 0 && (
                    <div className="homepage__search-category">
                      <div className="homepage__search-category-title">פרוטוקולים</div>
                      {searchResults.protocols.map((protocol, idx) => {
                        const globalIndex = searchResults.apps.length + idx;
                        return (
                          <div
                            key={protocol._id}
                            className={`homepage__search-item ${selectedSearchIndex === globalIndex ? 'selected' : ''}`}
                            onClick={() => handleSearchResultClick(protocol)}
                          >
                            <FaBook className="homepage__search-item-icon" />
                            <span className="homepage__search-item-text">
                              {protocol.name}
                              {protocol.hospital && <span className="homepage__search-item-subtitle"> - {protocol.hospital}</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {searchResults.cases.length > 0 && (
                    <div className="homepage__search-category">
                      <div className="homepage__search-category-title">מקרים רפואיים</div>
                      {searchResults.cases.map((caseItem, idx) => {
                        const globalIndex = searchResults.apps.length + searchResults.protocols.length + idx;
                        return (
                          <div
                            key={caseItem._id}
                            className={`homepage__search-item ${selectedSearchIndex === globalIndex ? 'selected' : ''}`}
                            onClick={() => handleSearchResultClick(caseItem)}
                          >
                            <FaBriefcaseMedical className="homepage__search-item-icon" />
                            <span className="homepage__search-item-text">
                              {caseItem.name}
                              {caseItem.hospital && <span className="homepage__search-item-subtitle"> - {caseItem.hospital}</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {searchResults.checklists.length > 0 && (
                    <div className="homepage__search-category">
                      <div className="homepage__search-category-title">רשימות</div>
                      {searchResults.checklists.map((checklist, idx) => {
                        const globalIndex = searchResults.apps.length + searchResults.protocols.length + searchResults.cases.length + idx;
                        return (
                          <div
                            key={checklist._id}
                            className={`homepage__search-item ${selectedSearchIndex === globalIndex ? 'selected' : ''}`}
                            onClick={() => handleSearchResultClick(checklist)}
                          >
                            <FaClipboardList className="homepage__search-item-icon" />
                            <span className="homepage__search-item-text">
                              {checklist.name}
                              {checklist.hospital && <span className="homepage__search-item-subtitle"> - {checklist.hospital}</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="homepage__search-no-results">לא נמצאו תוצאות</div>
              )}
            </div>
          )}
        </div>

        {/* Tiles Grid */}
        <div className="homepage__tiles" ref={tilesRef}>
          {appsLoading ? (
            // Skeleton loading state
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="homepage__tile">
                <Skeleton variant="circular" width={60} height={60} style={{ marginBottom: '10px' }} />
                <Skeleton variant="text" width="80%" height={24} />
              </div>
            ))
          ) : (
            tiles
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((tile, index) => {
                const config = getTileConfig(tile.type, tile.appId);
                if (!config) return null;

                // Generate a unique key
                const tileKey = tile.type === 'app'
                  ? `app-${tile.appId?._id || tile.appId}`
                  : `${tile.type}-${index}`;

                return (
                  <div
                    key={tileKey}
                    className="homepage__tile"
                    onClick={() => handleTileClick(config.url)}
                  >
                    <div className="homepage__tile-icon">
                      {config.icon}
                    </div>
                    <div className="homepage__tile-title">{config.title}</div>
                  </div>
                );
              })
          )}
        </div>

        {/* Configure Button */}
        <button
          ref={buttonRef}
          className={`homepage__config-button ${isButtonCompact ? 'compact' : ''}`}
          onClick={() => setShowConfigModal(true)}
          title="עריכת דף הבית"
        >
          <EditIcon />
          <span className="homepage__config-button-text">עריכת דף הבית</span>
        </button>

        {/* Configuration Modal */}
        {showConfigModal && (
          <div className="homepage__modal-overlay" onClick={() => setShowConfigModal(false)}>
            <div className="homepage__modal" onClick={(e) => e.stopPropagation()}>
              <div className="homepage__modal-header">
                <h2>התאמת דף הבית</h2>
                <button
                  className="homepage__modal-close"
                  onClick={() => setShowConfigModal(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="homepage__modal-body">
                <p className="homepage__modal-description">
                  בחר עד 6 אפליקציות
                </p>

                <div className="homepage__modal-tiles">
                  {appsLoading ? (
                    // Skeleton loading state for modal
                    Array.from({ length: 8 }).map((_, index) => (
                      <div key={index} className="homepage__modal-tile">
                        <Skeleton variant="circular" width={40} height={40} style={{ marginLeft: '10px' }} />
                        <Skeleton variant="text" width="70%" height={24} />
                      </div>
                    ))
                  ) : (
                    availableTileOptions.map((option) => {
                      const isSelected = isTileSelected(option.type, option.appId);
                      return (
                        <div
                          key={option.id}
                          className={`homepage__modal-tile ${isSelected ? 'selected' : ''}`}
                          onClick={(e) => toggleTile(option.type, option.appId, e)}
                        >
                          <div className="homepage__modal-tile-icon">
                            {option.iconName ? <DynamicFaIcon iconName={option.iconName} /> : (
                              option.type === 'protocols' ? <FaBook /> :
                              option.type === 'users' ? <FaUsers /> :
                              option.type === 'checklists' ? <FaClipboardList /> :
                              option.type === 'cases' ? <FaBriefcaseMedical /> :
                              option.type === 'departmentDocuments' ? <FaFolderOpen /> : null
                            )}
                          </div>
                          <div className="homepage__modal-tile-title">{option.title}</div>
                          {isSelected && (
                            <div className="homepage__modal-tile-check">
                              <FaCheck />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Limit Warning Popup */}
                {showLimitWarning && (
                  <div
                    className="homepage__limit-warning"
                    style={{
                      position: 'fixed',
                      left: `${warningPosition.x}px`,
                      top: `${warningPosition.y - 60}px`,
                      transform: 'translateX(-50%)',
                    }}
                  >
                    ניתן לבחור עד 6 אפליקציות בלבד
                  </div>
                )}
              </div>

              <div className="homepage__modal-footer">
                <LoadingSubmitButton
                  type="button"
                  onClick={handleSaveConfig}
                  style={confirmButton}
                  isLoading={isSaving}
                  text="שמור"
                  loadingText="שומר..."
                  disabled={isSaving}
                />
                <button
                  type="button"
                  style={cancelButton}
                  onClick={() => setShowConfigModal(false)}
                  disabled={isSaving}
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CommonLayout>
  );
};

export default Homepage;
